import React, { useEffect, useState } from "react";
import { api, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { X } from "@phosphor-icons/react";
import { CATEGORIES, PRIORITIES, tagClass } from "@finance/lib/actionColors";

const QUICK_TAGS = ["Urgent", "Waiting", "Blocked", "This Week", "TRA", "VAT"];

export default function ActionDialog({ open, onOpenChange, users, onSaved, initial }) {
  const [form, setForm] = useState({});
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (open) {
      setTagInput("");
      setForm(
        initial
          ? { ...initial, tags: initial.tags || [] }
          : {
              category: "Follow-up",
              assigned_to: users?.[0]?.id || "",
              details: "",
              amount: "",
              due_date: "",
              visibility: "Private",
              status: "Pending",
              priority: "Medium",
              tags: [],
              notes: "",
            }
      );
    }
  }, [open, initial, users]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = (raw) => {
    const t = (raw || "").trim().replace(/^#/, "");
    if (!t) return;
    setForm((f) => {
      const cur = f.tags || [];
      if (cur.some((x) => x.toLowerCase() === t.toLowerCase())) return f;
      return { ...f, tags: [...cur, t] };
    });
    setTagInput("");
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: (f.tags || []).filter((x) => x !== t) }));

  const save = async () => {
    if (!form.details || !form.assigned_to) {
      toast.error("Details and assignee are required");
      return;
    }
    const payload = { ...form, amount: form.amount === "" ? null : Number(form.amount), tags: form.tags || [] };
    try {
      if (initial?.id) await api.put(`/actions/${initial.id}`, payload);
      else await api.post("/actions", payload);
      toast.success("Action saved");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm("Delete this action? It will move to Deleted Items and can be restored.")) return;
    try {
      await api.delete(`/actions/${initial.id}`);
      toast.success("Moved to Deleted Items");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="action-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{initial?.id ? "Edit Action" : "New Action"}</DialogTitle>
          <DialogDescription>Capture a task with a category, priority, coloured tags and an optional due date.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="lx-label">Details</Label>
            <Textarea data-testid="action-details" value={form.details || ""} onChange={(e) => set("details", e.target.value)} className="mt-1" rows={2} />
          </div>
          <div>
            <Label className="lx-label">Assignee</Label>
            <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger className="mt-1" data-testid="action-assignee"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {users?.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role_code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="lx-label">Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="mt-1" data-testid="action-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="lx-label">Priority</Label>
            <Select value={form.priority || "Medium"} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger className="mt-1" data-testid="action-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="lx-label">Amount (optional)</Label>
            <Input type="number" data-testid="action-amount" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="lx-label">Due Date / Deadline</Label>
            <DateField data-testid="action-due" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="lx-label">Visibility</Label>
            <Select value={form.visibility} onValueChange={(v) => set("visibility", v)}>
              <SelectTrigger className="mt-1" data-testid="action-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Private">Private</SelectItem>
                <SelectItem value="Public">Public</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2">
            <Label className="lx-label">Tags</Label>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-sm border border-gray-200 p-2">
              {(form.tags || []).map((t) => (
                <span key={t} className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-medium ${tagClass(t)}`} data-testid={`dialog-tag-${t}`}>
                  #{t}
                  <button type="button" onClick={() => removeTag(t)} className="hover:text-red-500" data-testid={`dialog-tag-remove-${t}`}><X size={11} /></button>
                </span>
              ))}
              <input
                data-testid="action-tag-input"
                className="min-w-[100px] flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-gray-400"
                placeholder="Type a tag, press Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); } }}
              />
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {QUICK_TAGS.map((t) => (
                <button key={t} type="button" data-testid={`quick-tag-${t}`} onClick={() => addTag(t)} className="rounded-sm border border-dashed border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-500 hover:border-[#1B4383] hover:text-[#1B4383]">+ {t}</button>
              ))}
            </div>
          </div>

          <div className="col-span-2">
            <Label className="lx-label">Notes</Label>
            <Textarea data-testid="action-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1" rows={2} />
          </div>
        </div>
        <DialogFooter>
          {initial?.id && (
            <Button variant="outline" className="mr-auto border-red-200 text-red-600 hover:bg-red-50" data-testid="action-delete" onClick={del}>Delete</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="action-save" className="bg-[#1B4383] hover:bg-[#122E5C]" onClick={save}>Save Action</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
